import { Link } from 'react-router'
import { IconChevron, VinkItem } from '../components/Iconen'

/*
 * P2 — Beoordeling. Tekst en opbouw letterlijk uit
 * legacy/current/beoordeling.html (CR-04 t/m CR-06, FR-17).
 */

function Stap({
  nummer,
  kop,
  samenvatting,
  open = false,
  children,
}: {
  nummer: number
  kop: string
  samenvatting: string
  open?: boolean
  children: React.ReactNode
}) {
  return (
    <li className="stap reveal">
      <details open={open}>
        <summary>
          <span className="stap__num">{nummer}</span>
          <span className="stap__kop">
            <b>{kop}</b>
            <span>{samenvatting}</span>
          </span>
          <span className="stap__chev" aria-hidden="true">
            <IconChevron />
          </span>
        </summary>
        <div className="stap__body">{children}</div>
      </details>
    </li>
  )
}

export default function Beoordeling() {
  return (
    <>
      <div className="page-head">
        <div className="wrap reveal">
          <span className="eyebrow">Stappenplan</span>
          <h1>Zo werkt het: van aanvraag tot advies</h1>
          <p className="lede">
            Hieronder leest u meer uitleg over de stappen in het proces.{' '}
            <strong>De tarieven zijn voor alle type aanvragen gelijk.</strong>
          </p>
        </div>
      </div>

      {/* Stappenplan */}
      <section className="section" style={{ paddingTop: '40px' }}>
        <div className="wrap">
          <ol className="stappen">
            <Stap
              nummer={1}
              kop="Controleer uw situatie"
              samenvatting="Bekijk of u mogelijk in aanmerking komt voor een gehandicaptenparkeerkaart"
              open
            >
              <p>
                Een gehandicaptenparkeerkaart is bedoeld voor mensen met een
                langdurige beperking in hun mobiliteit. Kijk eerst welke kaart
                bij uw situatie past. Twijfelt u? Kies dan de kaart die het
                dichtst in de buurt komt; onze arts beoordeelt tijdens de
                keuring waar u werkelijk voor in aanmerking komt.
              </p>
              <ul className="crit__list">
                <VinkItem>
                  <b>GPK-B</b>: u bent beperkt in uw loopvermogen, maar kunt nog
                  wel zelf autorijden.
                </VinkItem>
                <VinkItem>
                  <b>GPK-P</b>: u moet van deur tot deur vervoerd worden en
                  heeft continu hulp nodig.
                </VinkItem>
                <VinkItem>
                  <b>GPK-B+P</b>: u rijdt afwisselend zelf en wordt afwisselend
                  vervoerd.
                </VinkItem>
              </ul>
            </Stap>

            <Stap
              nummer={2}
              kop="Aanvraag indienen"
              samenvatting="Bij ons of via uw gemeente, afhankelijk van hoe uw gemeente het heeft ingericht"
            >
              <p>
                Hoe u de gehandicaptenparkeerkaart aanvraagt, verschilt per
                gemeente. Sommige gemeenten laten u de aanvraag rechtstreeks bij
                ons doen; andere regelen de aanvraag via hun eigen loket en
                schakelen ons in voor het medisch advies. Weet u niet welke
                route voor u geldt?{' '}
                <Link to="/contact">Neem contact met ons op</Link>, dan zoeken
                wij het voor u uit.
              </p>
              <p>
                Vraagt u rechtstreeks bij ons aan? Dan vult u het
                aanvraagformulier in met uw persoonlijke gegevens, uw gemeente
                en of het om een eerste aanvraag of een verlenging gaat. Het
                formulier is bewust kort gehouden: alleen wat wij echt nodig
                hebben voor de beoordeling. Wij vragen geen BSN en slaan geen
                medische gegevens op de website op.
              </p>
              {/*
                De bestaande site zegt hier "Het tarief hangt af van uw gemeente".
                Dat klopt niet meer: het bedrag is voor iedereen gelijk. Hoe de
                tarieven verder op de site worden gepresenteerd ligt nog open
                (O-02), dus hier staat alleen wat feitelijk juist is en waar de
                bezoeker het bedrag ziet.
              */}
              <p>
                <b>Betaal veilig met iDEAL.</b> Direct na het verzenden van uw
                aanvraag rekent u af met iDEAL. Het tarief is voor alle
                kaarttypen en alle gemeenten gelijk; u ziet het exacte bedrag op
                de knop voordat u de aanvraag verstuurt. Lukt de betaling niet
                of breekt u af? Dan kunt u het opnieuw proberen zonder uw
                gegevens nogmaals in te vullen.
              </p>
            </Stap>

            <Stap
              nummer={3}
              kop="Plan uw afspraak"
              samenvatting="Wij nemen contact met u op om een beoordeling in te plannen"
            >
              <p>
                Na de betaling nemen wij contact met u op om een moment te
                plannen dat u schikt. U ontvangt een bevestiging met de datum en
                tijd van uw afspraak, en tijdig daarvoor nog een herinnering.
              </p>
              <p>
                De beoordeling kan plaatsvinden op locatie, via videobellen of
                bij u aan huis. Annuleert u tot 24 uur voor de afspraak, dan
                brengen wij geen kosten in rekening.
              </p>
            </Stap>

            <Stap
              nummer={4}
              kop="Bereid de beoordeling voor"
              samenvatting="Verzamel de relevante informatie voor uw medische beoordeling"
            >
              <p>
                Een goede voorbereiding helpt de arts om een volledig beeld van
                uw situatie te krijgen. Verzamel daarom vooraf alle medische
                informatie die relevant kan zijn voor uw mobiliteitsbeperkingen.
              </p>
              <p className="stap__sub">
                <b>Benodigdheden</b>
              </p>
              <ul className="crit__list">
                <VinkItem>
                  Geldig identiteitsbewijs, rijbewijs of paspoort
                </VinkItem>
                <VinkItem>Medicatieoverzicht</VinkItem>
                <VinkItem>Brieven van specialist(en)</VinkItem>
                <VinkItem>Medische rapportages of behandelverslagen</VinkItem>
                <VinkItem>
                  Hulpmiddelen die u gebruikt (indien van toepassing)
                </VinkItem>
              </ul>
            </Stap>

            <Stap
              nummer={5}
              kop="Medische beoordeling"
              samenvatting="Een arts onderzoekt uw medische situatie en mobiliteit"
            >
              <p>
                Tijdens de afspraak bespreekt de arts uw situatie en beoordeelt
                hij of zij uw loopvermogen en mobiliteit. De beoordeling vindt
                onafhankelijk plaats en volgens de geldende richtlijnen.
              </p>
              <p>
                <b>
                  Indien mogelijk, heeft het de voorkeur dat u medische
                  documentatie meeneemt
                </b>
                , geprint of digitaal, naar de afspraak ter ondersteuning van uw
                medische situatie.
              </p>
            </Stap>

            <Stap
              nummer={6}
              kop="Advies en besluit"
              samenvatting="Het medisch advies wordt opgesteld en naar de gemeente gestuurd"
            >
              <p>
                Na de beoordeling stelt de arts het advies op; dit wordt
                doorgaans binnen 24 uur verwerkt en naar uw gemeente gestuurd.
                De arts licht de uitkomst direct aan u toe, zodat u weet waar u
                aan toe bent.
              </p>
              <p>
                De gemeente neemt op basis van het advies het uiteindelijke
                besluit over de toekenning van uw kaart.
              </p>
            </Stap>
          </ol>

          <div style={{ marginTop: '32px' }} className="reveal">
            <Link to="/#aanvraag" className="btn btn--coral btn--big">
              Vraag aan
            </Link>
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingBottom: 0 }}>
        <div className="wrap">
          {/* CR-05 */}
          <div className="infoband reveal">
            <h2>Geldig in heel Europa</h2>
            <p>
              Als u een GPK toegekend krijgt, krijgt u een Europese
              gehandicaptenparkeerkaart. Hiermee kunt u parkeren op
              gehandicaptenparkeerplaatsen in alle landen van de Europese Unie
              (EU). De kaart is ook geldig in Liechtenstein, Noorwegen, IJsland
              en Zwitserland.
            </p>
          </div>
          {/* CR-06 / FR-17 */}
          <div className="infoband reveal">
            <h2>Parkeerregels en tarieven per gemeente</h2>
            <p>
              Afhankelijk van het land, maar ook de gemeente, worden er
              verschillende tarieven en zelfs vrijstellingen van parkeerkosten
              gehanteerd. Op de volgende website is er een onafhankelijke
              onderneming die een overzicht heeft gepubliceerd van de regels per
              gemeente:{' '}
              <a
                href="https://www.gpkwijs.nl/"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.gpkwijs.nl
              </a>
              .
            </p>
            <p className="ext">
              Deze link opent in een nieuw tabblad. GPKwijs is een
              onafhankelijke externe partij; wij zijn niet verantwoordelijk voor
              de inhoud.
            </p>
          </div>
        </div>
      </section>
    </>
  )
}
